import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone, Search, Info, Building2, Ribbon, HeartPulse, Send } from "lucide-react";
import { KineticHeading, FadeUp, Reveal, HeroBackdrop, scrollToTarget } from "@/components/site/motion";
import { trackClick } from "@/lib/api";
import { getPlanBySlug, PLANS } from "@/lib/plans";
import { BUSINESS } from "@/lib/site";
import { Seo } from "@/components/site/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ContactForm } from "@/components/site/ContactForm";
import { IndemnityComparison } from "@/components/site/IndemnityComparison";
import { DentistChecker, PROVIDER_DISCLAIMER } from "@/components/site/DentistChecker";
import { ACA_COMPARE_QUIZ, HOSPITAL_EXPENSE_URL, LIFE_CANCER_CI_URL } from "@/lib/indemnityQuiz";

const INDEMNITY_URL =
  "https://customer.enroll.natgenhealth.com/quick-quote/?agent=CfDJ8KcuJeU1UfVFhwatU8NLwQl7-pF18Le6lw2-62Tl1CzVtMNfxrBEKvkNzZ2Dh-y_cvEe2-wKp-uCdrKU1D1sEBIuKQ&product=fixed-benefit-indemnity";

const CANCER_ONLY_URL =
  "https://customer.enroll.natgenhealth.com/quick-quote/?agent=CfDJ8KcuJeU1UfVFhwatU8NLwQl7-pF18Le6lw2-62Tl1CzVtMNfxrBEKvkNzZ2Dh-y_cvEe2-wKp-uCdrKU1D1sEBIuKQ&product=cancer-only";

// Hospital Indemnity carrier quick links
const HOSPITAL_QUICK_LINKS = [
  {
    id: "allstate",
    carrier: "Allstate Health Solutions",
    product: "Hospital Indemnity",
    blurb: "Quick quote and online enrollment — Christian is pre-loaded as your agent.",
    url: "https://customer.enroll.natgenhealth.com/quick-quote/?agent=CfDJ8KcuJeU1UfVFhwatU8NLwQmcAIGfOiPxjHTVj4ywNGCd_nlFTLXzhpD2bGUj4aE0qDv92G-m4_qp5QwWS1HWtIk_1w&product=hospital-expense",
    buttonText: "Quote Allstate Hospital Indemnity",
  },
  {
    id: "uhone",
    carrier: "UnitedHealthcare (UHOne)",
    product: "Hospital Indemnity",
    blurb: "Shop hospital & doctor coverage underwritten by Golden Rule Insurance Company.",
    url: "https://shop.uhone.com/en/quote/census/hospitalindemnity?brokerid=AA5632842",
    banner: "https://www.uhone.com/ContentManagement/FileAttachment.ashx?FilePath=/Quoting_Link_Banner_HospitalIndemnity.jpg",
    buttonText: "Quote UnitedHealthcare Hospital Indemnity",
  },
];

// ACA supplemental comparison — hospital-only vs multi-use bundle.
const ACA_COMPARE_OPTIONS = [
  {
    id: "hospital",
    title: "Hospital-only indemnity",
    icon: Building2,
    tag: "Simpler · Single benefit",
    fitLines: [
      "You picked a bronze / high-deductible Marketplace plan",
      "Your biggest worry is a hospital admission or ER visit",
      "You want the lowest-priced supplemental option",
      "You already have life insurance elsewhere",
    ],
    ctaLabel: "See hospital-only plans",
    ctaUrl: HOSPITAL_EXPENSE_URL,
    testid: "aca-compare-hospital",
  },
  {
    id: "bundle",
    title: "Life + cancer + critical illness bundle",
    icon: Ribbon,
    tag: "Broader · Multi-benefit",
    fitLines: [
      "You want term life alongside health protection",
      "Family history of cancer, heart attack, or stroke concerns you",
      "You prefer one policy, one premium, one application",
      "You don't already carry standalone life insurance",
    ],
    ctaLabel: "See the bundle option",
    ctaUrl: LIFE_CANCER_CI_URL,
    testid: "aca-compare-bundle",
  },
];

// Cancer supplemental comparison — cancer-only vs multi-use bundle.
const CANCER_COMPARE_OPTIONS = [
  {
    id: "cancer-only",
    title: "Cancer-only lump-sum plan",
    icon: Ribbon,
    tag: "Focused · Single diagnosis",
    fitLines: [
      "Cancer is your top financial worry (family history, etc.)",
      "You already have hospital indemnity or term life coverage",
      "You want the leanest premium possible",
      "You'd rather stack a specialist plan than bundle everything",
    ],
    ctaLabel: "See cancer-only plans",
    ctaUrl: CANCER_ONLY_URL,
    testid: "cancer-compare-only",
  },
  {
    id: "cancer-bundle",
    title: "Life + cancer + critical illness bundle",
    icon: HeartPulse,
    tag: "Broader · Multi-benefit",
    fitLines: [
      "You want term life alongside cancer protection",
      "Heart attack or stroke risk also concerns you",
      "You prefer one policy, one premium, one application",
      "You don't already carry standalone life insurance",
    ],
    ctaLabel: "See the bundle option",
    ctaUrl: LIFE_CANCER_CI_URL,
    testid: "cancer-compare-bundle",
  },
];

const CANCER_COMPARE_QUIZ = [
  {
    id: "concern",
    question: "Which risk concerns you most?",
    answers: [
      { label: "Cancer specifically", scoreFor: "left" },
      { label: "Cancer, heart attack & stroke together", scoreFor: "right" },
    ],
  },
  {
    id: "life",
    question: "Do you already carry term life insurance?",
    answers: [
      { label: "Yes, I'm covered", scoreFor: "left" },
      { label: "No, or not enough", scoreFor: "right" },
    ],
  },
  {
    id: "shape",
    question: "How do you like to structure supplemental coverage?",
    answers: [
      { label: "Stack specialist plans", scoreFor: "left" },
      { label: "One combined policy", scoreFor: "right" },
    ],
  },
];

const BG_IMG =
  "https://images.unsplash.com/photo-1543253918-5a6777d56824?crop=entropy&cs=srgb&fm=jpg&q=85";

const DENTAL_PROVIDER_SEARCH = [
  {
    id: "ncd",
    name: "NCD Dental",
    buttonText: "Search & View Plans",
    url: "https://enrollment.ncd.com/783491",
    footnote: "Click 'Search Dental Providers' at the top of the enrollment page to check your dentist.",
  },
  {
    id: "delta",
    name: "Delta Dental",
    buttonText: "Search & View Plans",
    url: "https://www.deltadentalins.com/shopping/delta/get-a-quote?issuerCode=DELTA&brokerId=301896",
    footnote: "Christian is already listed as your broker on this page. Search the network inside the quote tool.",
  },
  {
    id: "humana",
    name: "Humana Dental",
    buttonText: "Search & View Plans",
    url: "https://www.humana.com/aoadv/1914661",
    footnote: "Click 'Find Care' at the top of the Humana page to see in-network local dentists.",
  },
  {
    id: "uhone",
    name: "UnitedHealthcare Dental",
    buttonText: "Search & View Plans",
    url: "https://shop.uhone.com/en/quote/census/dental?brokerid=AA5632842",
    banner: "https://www.uhone.com/ContentManagement/FileAttachment.ashx?FilePath=/dental_btn.jpg",
    footnote: "Underwritten by Golden Rule Insurance Company. Christian is pre-loaded as your broker on the UHOne quote page.",
  },
];

const QuizToForm = ({ onClick, testid }) => (
  <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[var(--brand-primary)]/30 bg-white px-5 py-4">
    <p className="flex-1 text-sm text-slate-700">
      Want Christian to sanity-check this result? Your quiz answer is attached to the message form below.
    </p>
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="cta-kinetic inline-flex items-center justify-center rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium px-5 h-10"
    >
      <Send className="w-4 h-4 mr-2" /> Send my result
    </button>
  </div>
);

const PlanDetail = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const plan = getPlanBySlug(slug);
  const [quizResult, setQuizResult] = useState(null);
  const [contactPrefill, setContactPrefill] = useState("");

  if (!plan) return <Navigate to="/" replace />;

  const Icon = plan.icon;
  const otherPlans = PLANS.filter((p) => p.slug !== plan.slug).slice(0, 3);
  const onQuizResult = (label) => (r) => setQuizResult(r ? `${r.title} (${label} quiz)` : null);
  const onCompareCta = (opt) => trackClick(opt.testid, opt.title, `/${plan.slug}`);
  const goToForm = () => {
    const el = document.getElementById("plan-contact-section");
    if (el) scrollToTarget(el);
  };
  const askChristian = (message) => {
    setContactPrefill(message);
    goToForm();
  };

  return (
    <div data-testid={`page-plan-${plan.slug}`}>
      <Seo
        title={`${plan.name} Plans in Santa Rosa Beach & the Emerald Coast`}
        description={`${plan.short} Local independent broker serving Santa Rosa Beach, Destin, Miramar Beach, Panama City Beach and Freeport, FL.`}
        path={`/${plan.slug}`}
      />
      {/* Hero */}
      <section
        className="relative isolate overflow-hidden"
        data-testid="plan-hero"
      >
        <HeroBackdrop image={BG_IMG} />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-28">
          <div className="max-w-3xl">
            <FadeUp>
              <Link
                to="/"
                className="eyebrow text-[var(--brand-secondary)] hover:text-white transition-colors"
                data-testid="plan-back-link"
              >
                ← All plans
              </Link>
              <div className="mt-5 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 text-white backdrop-blur-md">
                <Icon className="w-5 h-5" />
              </div>
            </FadeUp>
            <KineticHeading
              delay={0.15}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
              data-testid="plan-hero-heading"
              lines={[plan.name]}
            />
            <FadeUp delay={0.4}>
              <p className="mt-5 text-base sm:text-lg text-slate-100/90 max-w-2xl leading-relaxed">
                {plan.short}
              </p>
              <p className="mt-6 eyebrow text-[var(--brand-secondary)]">For: {plan.audience}</p>
            </FadeUp>
            <FadeUp delay={0.55} className="mt-10 flex flex-col sm:flex-row gap-3">
              {plan.slug === "dental" && (
                <button
                  type="button"
                  data-testid="plan-dentist-check-cta"
                  onClick={() => scrollToTarget(document.getElementById("dentist-check"))}
                  className="cta-kinetic inline-flex items-center justify-center rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white text-sm font-semibold px-7 h-12"
                >
                  <Search className="w-4 h-4 mr-2" /> Check my dentist
                </button>
              )}
              {plan.external_cta && (
                plan.external_cta.url.startsWith("/") ? (
                  <Link
                    to={plan.external_cta.url}
                    data-testid="plan-external-cta"
                    className="cta-kinetic inline-flex items-center justify-center rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white text-sm font-semibold px-7 h-12 transition-colors duration-200"
                  >
                    {plan.external_cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                ) : (
                  <a
                    href={plan.external_cta.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="plan-external-cta"
                    onClick={() => trackClick(`hero-${plan.slug}`, plan.external_cta.label, `/${plan.slug}`)}
                    className="cta-kinetic inline-flex items-center justify-center rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white text-sm font-semibold px-7 h-12 transition-colors duration-200"
                  >
                    {plan.external_cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                )
              )}
              <Link
                to={`/quote?plan=${encodeURIComponent(plan.name)}`}
                data-testid="plan-quote-cta"
                className="cta-kinetic inline-flex items-center justify-center rounded-full bg-white text-[var(--ink)] hover:bg-[var(--brand-secondary)] hover:text-white text-sm font-semibold px-7 h-12 transition-colors duration-200"
              >
                Get a {plan.name} quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                data-testid="plan-call-cta"
                className="cta-kinetic inline-flex items-center justify-center rounded-full border border-white/40 text-white hover:bg-white/10 text-sm font-medium px-7 h-12 transition-colors duration-200"
              >
                <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
              </a>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Hospital Indemnity: carrier quick links */}
      {plan.slug === "hospital-indemnity" && (
        <section
          className="bg-white border-b border-slate-200 py-16"
          data-testid="hospital-quick-links"
          aria-labelledby="hospital-quick-links-heading"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal className="max-w-2xl">
              <p className="eyebrow">Quick links</p>
              <h2
                id="hospital-quick-links-heading"
                className="mt-3 font-display text-3xl sm:text-4xl text-[var(--ink)] tracking-tight"
              >
                Quote &amp; enroll online in minutes
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Pick a carrier below to run a quick quote. Christian is already on
                file as your broker on every enrollment page, so you get the same
                personal service either way.
              </p>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {HOSPITAL_QUICK_LINKS.map((c, i) => (
                <Reveal
                  key={c.id}
                  delay={i * 0.1}
                  data-testid={`hospital-quick-link-card-${c.id}`}
                  className="card-lift rounded-2xl border border-slate-200 bg-white p-7 flex flex-col hover:border-[var(--brand-primary)] transition-colors"
                >
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                    <Building2 className="w-3.5 h-3.5" /> {c.product}
                  </div>
                  <h3 className="mt-4 font-display text-xl text-[var(--ink)]">{c.carrier}</h3>
                  <p className="mt-3 mb-6 text-sm text-slate-600 leading-relaxed">{c.blurb}</p>
                  {c.banner && (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`hospital-quick-link-banner-${c.id}`}
                      onClick={() => trackClick(`hospital-${c.id}`, `${c.carrier} banner`, "/hospital-indemnity")}
                      className="mb-6 block rounded-lg overflow-hidden border border-slate-200 hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={c.banner}
                        alt={`${c.carrier} — Shop for Hospital & Doctor Coverage`}
                        width="468"
                        height="60"
                        loading="lazy"
                        className="w-full h-auto"
                      />
                    </a>
                  )}
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`hospital-quick-link-button-${c.id}`}
                    onClick={() => trackClick(`hospital-${c.id}`, c.carrier, "/hospital-indemnity")}
                    className="mt-auto inline-flex items-center justify-center w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium px-5 h-11 transition-colors"
                  >
                    {c.buttonText} <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Reveal>
              ))}
            </div>
            <p className="mt-8 text-[11px] leading-relaxed text-slate-500 max-w-3xl">
              Hospital indemnity is a supplemental, fixed-benefit product — not
              comprehensive health insurance and not a substitute for a Marketplace or
              Medicare plan. Benefits, eligibility, and exclusions vary by carrier and
              state. You will be taken to the carrier’s secure quoting site.
            </p>
          </div>
        </section>
      )}

      {/* Dental-only: Dentist network checker */}
      {plan.slug === "dental" && (
        <section
          id="dentist-check"
          className="bg-[var(--bg-secondary)] wave-divider border-b border-slate-200 py-14 sm:py-20 scroll-mt-24"
          data-testid="dentist-check-section"
          aria-labelledby="dentist-check-heading"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            <Reveal className="lg:col-span-2">
              <p className="eyebrow">Dentist check</p>
              <h2 id="dentist-check-heading" className="mt-3 font-display text-3xl sm:text-4xl text-[var(--ink)] tracking-tight">
                Which plan does my dentist accept?
              </h2>
              <p className="mt-4 text-slate-700 leading-relaxed">
                Type your dentist and I’ll check the four dental carriers I sell —
                NCD, Delta, Humana and UnitedHealthcare — against my local Emerald
                Coast list. Anything unclear routes straight to me.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[var(--brand-primary)]" /> Results in seconds, no sign-up</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[var(--brand-primary)]" /> Official carrier finder links on every result</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[var(--brand-primary)]" /> One tap to have Christian confirm by hand</li>
              </ul>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-3">
              <DentistChecker onAskChristian={askChristian} />
            </Reveal>
          </div>
        </section>
      )}

      {/* Detail body */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <Reveal className="lg:col-span-2">
          <p className="eyebrow">Overview</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[var(--ink)] tracking-tight">
            What’s included in {plan.name}
          </h2>
          <p className="mt-6 text-slate-700 leading-relaxed">{plan.details}</p>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {plan.bullets.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-3 p-5 rounded-xl border border-slate-200 bg-white"
                data-testid={`plan-bullet-${i}`}
              >
                <span className="mt-0.5 inline-flex w-7 h-7 rounded-full bg-[var(--bg-secondary)] items-center justify-center text-[var(--brand-primary)]">
                  <Check className="w-4 h-4" />
                </span>
                <span className="text-sm text-slate-700">{b}</span>
              </li>
            ))}
          </ul>

          <h3 className="mt-16 font-display text-2xl text-[var(--ink)]">Frequently asked</h3>
          <Accordion type="single" collapsible className="mt-6" data-testid="plan-faq">
            {plan.faqs.map((f, i) => (
              <AccordionItem value={`faq-${i}`} key={i} data-testid={`plan-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-base text-[var(--ink)] hover:text-[var(--brand-primary)]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>

        <aside className="lg:sticky lg:top-24 self-start space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <p className="eyebrow">Talk to Christian</p>
            <h3 className="mt-3 font-display text-xl text-[var(--ink)]">
              Have a question about {plan.name}?
            </h3>
            <p className="mt-3 text-sm text-slate-600">
              Send a note and I’ll respond personally — usually same-day.
            </p>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="plan-side-call"
              className="mt-5 inline-flex items-center justify-center w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium px-5 h-11 transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
            </a>
            {plan.external_cta && (
              plan.external_cta.url.startsWith("/") ? (
                <Link
                  to={plan.external_cta.url}
                  data-testid="plan-side-external-cta"
                  className="mt-3 inline-flex items-center justify-center w-full rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white text-sm font-semibold px-5 h-11 transition-colors"
                >
                  {plan.external_cta.label}
                </Link>
              ) : (
                <a
                  href={plan.external_cta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="plan-side-external-cta"
                  onClick={() => trackClick(`side-${plan.slug}`, plan.external_cta.label, `/${plan.slug}`)}
                  className="mt-3 inline-flex items-center justify-center w-full rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white text-sm font-semibold px-5 h-11 transition-colors"
                >
                  {plan.external_cta.label}
                </a>
              )
            )}
            <Link
              to={`/quote?plan=${encodeURIComponent(plan.name)}`}
              data-testid="plan-side-quote"
              className="mt-3 inline-flex items-center justify-center w-full rounded-full border border-slate-200 text-[var(--ink)] hover:border-[var(--brand-primary)] text-sm font-medium px-5 h-11 transition-colors"
            >
              Start a quote
            </Link>
          </div>
          <div className="rounded-2xl bg-[var(--ink)] text-white p-7">
            <p className="eyebrow text-[var(--brand-secondary)]">Other plans</p>
            <ul className="mt-4 space-y-3 text-sm">
              {otherPlans.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/${p.slug}`}
                    data-testid={`plan-side-other-${p.slug}`}
                    className="text-slate-200 hover:text-white flex items-center justify-between"
                  >
                    <span>{p.name}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>

      {/* Dental-only: Provider Search */}
      {plan.slug === "dental" && (
        <section
          className="bg-white border-y border-slate-200 py-20"
          data-testid="dental-provider-search"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="max-w-2xl">
              <p className="eyebrow">Provider search</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[var(--ink)] tracking-tight">
                Search each carrier’s official directory
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Select a carrier below to search local dentists on the Emerald
                Coast. These are the primary networks for the plans offered on
                this page — and the final word on who’s in network.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {DENTAL_PROVIDER_SEARCH.map((c, i) => (
                <Reveal
                  key={c.id}
                  delay={i * 0.08}
                  data-testid={`dental-provider-card-${c.id}`}
                  className="card-lift rounded-2xl border border-slate-200 bg-white p-7 flex flex-col hover:border-[var(--brand-primary)]"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[var(--bg-secondary)] text-[var(--brand-primary)]">
                    <Search className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 font-display text-xl text-[var(--ink)]">
                    {c.name}
                  </h3>
                  {c.banner && (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`dental-provider-banner-${c.id}`}
                      onClick={() => trackClick(`dental-${c.id}`, `${c.name} banner`, "/dental")}
                      className="mt-5 block rounded-lg overflow-hidden border border-slate-200 hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={c.banner}
                        alt={`${c.name} — get a quote`}
                        width="468"
                        height="60"
                        loading="lazy"
                        className="w-full h-auto"
                      />
                    </a>
                  )}
                  <span className="flex-1" aria-hidden />
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`dental-provider-button-${c.id}`}
                    onClick={() => trackClick(`dental-${c.id}`, c.name, "/dental")}
                    className="mt-6 inline-flex items-center justify-center w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium px-5 h-11 transition-colors"
                  >
                    {c.buttonText}
                  </a>
                  <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                    {c.footnote}
                  </p>
                </Reveal>
              ))}
            </div>
            <p className="mt-8 text-[11px] leading-relaxed text-slate-500 max-w-3xl" data-testid="dental-provider-disclaimer">
              {PROVIDER_DISCLAIMER}
            </p>
          </div>
        </section>
      )}

      {/* ACA-only: Optional supplemental indemnity module (secondary CTA) */}
      {plan.slug === "aca-health" && (
        <section
          className="bg-white border-y border-slate-200 py-16"
          data-testid="aca-indemnity-module"
          aria-labelledby="indemnity-heading"
        >
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <div className="rounded-2xl border border-slate-200 bg-[var(--bg-secondary)]/60 p-8 sm:p-10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                <Info className="w-3.5 h-3.5" /> Optional · Supplemental only
              </div>
              <h2
                id="indemnity-heading"
                className="mt-3 font-display text-2xl sm:text-3xl text-[var(--ink)] tracking-tight"
              >
                Optional help with high deductibles
              </h2>
              <p className="mt-5 text-slate-700 leading-relaxed text-[0.95rem] max-w-3xl">
                Many Marketplace individual plans keep monthly premiums lower by pairing
                them with a higher deductible. An optional hospital or fixed-indemnity
                plan is not major medical coverage and is not a substitute for a
                Marketplace plan. It may pay a set cash benefit for covered events — such
                as a hospital stay — that you can use toward out-of-pocket costs while
                your Marketplace deductible is still in play. Ask us whether an
                indemnity option makes sense alongside the plan you choose.
              </p>

              {/* Comparison widget + mini-quiz — hospital-only vs multi-use bundle */}
              <div className="mt-8">
                <IndemnityComparison
                  testid="aca-indemnity-compare"
                  options={ACA_COMPARE_OPTIONS}
                  quiz={ACA_COMPARE_QUIZ}
                  onResult={onQuizResult("ACA")}
                  onCtaClick={onCompareCta}
                  fallback={{
                    label: "See all optional indemnity options",
                    url: INDEMNITY_URL,
                    testid: "aca-indemnity-cta",
                  }}
                />
                {quizResult && <QuizToForm onClick={goToForm} testid="aca-quiz-to-form" />}
              </div>

              <p
                className="mt-8 text-[11px] leading-relaxed text-slate-500"
                data-testid="aca-indemnity-disclaimer"
              >
                These are fixed-indemnity / hospital indemnity and specified-disease
                products, not comprehensive health insurance and not Qualified Health
                Plans. They do not satisfy the requirement to maintain minimum
                essential coverage. Benefits are paid as fixed dollar amounts and are
                not based on your actual medical bill. You remain responsible for
                deductibles, copays, coinsurance, and all other costs under your
                Marketplace plan. Eligibility, waiting periods, exclusions, and benefit
                amounts vary. "Typically fits if…" scenarios and the "Which fits me?"
                quiz are general guidance only, not personalized advice. This is not a
                government website and is not affiliated with CMS, HealthCare.gov, or
                the Marketplace.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Cancer Plans: Optional Life + Cancer + Critical Illness bundle (secondary) */}
      {plan.slug === "cancer-plans" && (
        <section
          className="bg-white border-y border-slate-200 py-16"
          data-testid="cancer-bundle-module"
          aria-labelledby="cancer-bundle-heading"
        >
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <div className="rounded-2xl border border-slate-200 bg-[var(--bg-secondary)]/60 p-8 sm:p-10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                <Info className="w-3.5 h-3.5" /> Optional · Supplemental only
              </div>
              <h2
                id="cancer-bundle-heading"
                className="mt-3 font-display text-2xl sm:text-3xl text-[var(--ink)] tracking-tight"
              >
                Cancer-only or a broader bundle?
              </h2>
              <p className="mt-5 text-slate-700 leading-relaxed text-[0.95rem] max-w-3xl">
                Some clients want a lean cancer-only lump-sum plan. Others prefer a
                single supplemental policy that also includes term life and critical
                illness. Both are supplemental only and neither is comprehensive
                health insurance or a substitute for a Marketplace plan. Ask us
                whether either makes sense alongside the coverage you already have.
              </p>

              <div className="mt-8">
                <IndemnityComparison
                  testid="cancer-compare"
                  options={CANCER_COMPARE_OPTIONS}
                  quiz={CANCER_COMPARE_QUIZ}
                  onResult={onQuizResult("Cancer")}
                  onCtaClick={onCompareCta}
                />
                {quizResult && <QuizToForm onClick={goToForm} testid="cancer-quiz-to-form" />}
              </div>

              <p
                className="mt-8 text-[11px] leading-relaxed text-slate-500"
                data-testid="cancer-bundle-disclaimer"
              >
                These are specified-disease / critical illness and supplemental life
                products, not comprehensive health insurance and not Qualified Health
                Plans. They do not satisfy the requirement to maintain minimum
                essential coverage. Benefits are paid as fixed dollar amounts on
                covered diagnoses and are not based on your actual medical bill. You
                remain responsible for deductibles, copays, coinsurance, and all other
                costs under your primary health plan. Eligibility, waiting periods,
                exclusions, and benefit amounts vary. "Typically fits if…" scenarios
                and the "Which fits me?" quiz are general guidance only, not
                personalized advice. This is not a government website and is not
                affiliated with CMS, HealthCare.gov, or the Marketplace.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Contact form at bottom */}
      <section id="plan-contact-section" className="bg-[var(--bg-secondary)] py-20" data-testid="plan-contact-section">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="eyebrow text-center">Send a message</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-center text-[var(--ink)]">
            Not ready for a full quote?
          </h2>
          <p className="mt-3 text-center text-slate-600 max-w-xl mx-auto">
            Ask anything — eligibility, networks, drug formulary checks. No obligation.
          </p>
          <div className="mt-10">
            <ContactForm defaultPlan={plan.name} quizResult={quizResult} prefillMessage={contactPrefill} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlanDetail;
